
export type ViewType = 'feed' | 'myActivity' | 'applicationDetail' | 'manageApplicants' | 'applicantList' | 'createOpportunity' | 'editOpportunity' | 'network' | 'profile' | 'messages' | 'pumpUpTheVolume' | 'opportunityDetail' | 'notifications' | 'feedback' | 'terms' | 'privacy' | 'cookies' | 'recommended' | 'calendar' | 'bandPage' | 'settings' | 'forgotPassword' | 'myBands' | 'myLineups' | 'myFlyers' | 'lineupEditor' | 'flyerPreview' | 'manageBand' | 'editProfile' | 'profileManager' | 'seedDashboard' | 'adminDashboard' | 'proDashboard' | 'myProjects' | 'fanList' | 'endorserList' | 'search' | 'sessions' | 'emailVerify';

export type UserRole = 'Musician' | 'Singer' | 'DJ' | 'Actor' | 'Director' | 'Dancer' | 'Performer' | 'Choreographer' | 'Model' | 'Comedian' | 'Writer' | 'Painter' | 'Visual Artist' | 'Venue' | 'Live Club' | 'Theatre' | 'Cinema' | 'Festival' | 'Label' | 'Radio' | 'Movie/TV Production' | 'Casting Agency' | 'Event Agency' | 'Publisher' | 'Producer' | 'Audio Service' | 'Recording Studio' | 'Music Store' | 'Luthier' | 'Sound Engineer' | 'Light Designer' | 'Filmmaker / Videographer' | 'Photographer' | 'Make-Up Artist (MUA)' | 'Costume Designer / Stylist' | 'Backline Rental' | 'Graphic Designer' | 'Music School' | 'Acting Academy' | 'Dance School' | 'Cultural Association' | 'Vocal Coach' | 'Music Enthusiast' | 'Music Addict' | 'Live Music Fan' | 'Concert Goer';

export const USER_ROLES: UserRole[] = [
    'Musician', 'Singer', 'DJ', 'Actor', 'Director', 'Dancer', 'Performer', 'Choreographer', 'Model', 'Comedian', 'Writer', 'Painter', 'Visual Artist',
    'Venue', 'Live Club', 'Theatre', 'Cinema', 'Festival', 'Label', 'Radio', 'Movie/TV Production', 'Casting Agency', 'Event Agency', 'Publisher', 'Producer',
    'Audio Service', 'Recording Studio', 'Music Store', 'Luthier', 'Sound Engineer', 'Light Designer', 'Filmmaker / Videographer', 'Photographer', 'Make-Up Artist (MUA)', 'Costume Designer / Stylist', 'Backline Rental', 'Graphic Designer',
    'Music School', 'Acting Academy', 'Dance School', 'Cultural Association', 'Vocal Coach',
    'Music Enthusiast', 'Music Addict', 'Live Music Fan', 'Concert Goer'
];

export type UserCluster = 'talent' | 'business' | 'service' | 'edu' | 'fan' | 'multi';

export type AvailabilityStatus = 'available' | 'day_off' | 'busy' | 'neutral';

export interface User {
    id?: string;
    handle: string;
    name: string;
    email: string;
    avatarUrl: string;
    bannerUrl?: string;
    title: string;
    city: string;
    region?: string;
    country?: string;
    roles?: UserRole[];
    role?: UserRole; 
    instruments?: string[];
    genres?: string[];
    projects?: Project[];
    bandIds?: string[];
    collaborations?: string;
    travelPlans?: TravelPlan[];
    endorsements?: string[];
    endorsedBy?: string[];
    needsOnboarding?: boolean;
    subscriptionTier?: string;
    collaborators?: string[];
    preferences?: {
        notifications?: {
            messages?: boolean;
            endorsements?: boolean;
            applications?: boolean;
            comments?: boolean;
            statusChanges?: boolean;
            matches?: boolean;
        };
        visibility?: {
            stealthMode?: boolean;
            hideFromSearch?: boolean;
            restrictedProfile?: boolean;
        };
        messaging?: MessagingPreference;
    };
    wages?: Wage[];
    availability?: { [date: string]: AvailabilityStatus };
    photos?: Photo[];
    embeddedMedia?: EmbeddedMedia[];
    socialLinks?: {
        facebook?: string;
        instagram?: string;
        spotify?: string;
        youtube?: string;
        [key: string]: string | undefined;
    };
    isSeed?: boolean;
    isAdmin?: boolean;
    referralCode?: string;
    accountStatus?: AccountStatus;
    trialEndsAt?: string;
    referredBy?: string;
    fans?: string[];
    rider?: string;
    riderFiles?: RiderFile[];
    emailVerified?: boolean;
}

export type MessagingPreference = 'all' | 'pro_network' | 'pro_only' | 'network_only' | 'restricted';
export type AccountStatus = 'active' | 'frozen' | 'deleted';

export interface Project {
    id: string;
    name: string;
    city: string;
    region?: string;
    country?: string;
}

export interface TravelPlan {
    id: string;
    city: string;
    region?: string;
    country?: string;
    startDate: string;
    endDate: string;
}

export interface Wage {
    id: string;
    currency: string;
    amount: number;
    rateType: string;
}

export interface Photo {
    id: string;
    url: string;
}

export interface EmbeddedMedia {
    id: string;
    type: 'youtube' | 'spotify' | 'soundcloud' | 'unknown';
    url: string;
    title?: string;
}

export interface Session {
    id: string;
    type: 'solo' | 'base' | 'collab';
    layout?: 'split-v' | 'split-h';
    creator: { handle: string; name: string; avatarUrl: string };
    title: string;
    description: string;
    videoUrl: string;
    thumbnailUrl: string;
    isPublic: boolean;
    genre?: string;
    instrumentNeeded?: string;
    timestamp: string;
    likes: number;
    shares: number;
    fileKey?: string;
    fileKeyB?: string;
    // Per le collab
    creatorB?: { handle: string; name: string; avatarUrl: string };
    videoUrlB?: string;
    parentSessionId?: string;
}

export interface PublishedSession extends Session {
    type: 'collab';
}

export interface AttendeeEntry {
    handle: string;
    name: string;
    avatarUrl: string;
    timestamp: string;
}

export interface Opportunity {
    id: string;
    author: Partial<User> & { 
        handle: string; 
        name: string; 
        avatarUrl: string; 
        title: string;
        preferences?: { visibility?: { stealthMode?: boolean } }; 
    };
    title: string;
    description: string;
    type: 'Job' | 'Gig' | 'Event' | 'Jam Session' | 'DJ Needed' | 'Musician Needed' | 'Audition' | 'Showcase' | 'Live Show' | 'Stage Play' | 'Movie' | 'Short Movie' | 'TV Show' | 'Open Mic' | 'DJ set' | 'Stand Up Comedy' | 'Workshop' | 'Presentation' | 'Vernissage' | 'Collective Exhibition';
    location: string;
    region?: string;
    country?: string;
    address?: string;
    coordinates?: { lat: number; lng: number };
    instrument?: string;
    genre?: string;
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    timezone?: string;
    salary?: Salary;
    likes: number;
    likedBy: string[];
    comments: number;
    amplifies: number;
    amplifiedBy: { handle: string; timestamp: string }[];
    attendees?: AttendeeEntry[];
    attendeeCount?: number;
    applicationCount?: number;
    applications: Application[];
    status: 'open' | 'closed';
    visibility: 'public' | 'private';
    invitedUsers?: string[];
    isOpenCall?: boolean;
    invitedBands?: string[];
    maxCapacity?: number;
    imageUrl?: string;
    showOnLandingPage?: boolean;
    roleNeeded?: UserRole;
    recentAmplifiers?: Partial<User>[];
    timestamp: string;
}

export const OPPORTUNITY_TYPES: Opportunity['type'][] = ['Job', 'Gig', 'Event', 'Jam Session', 'DJ Needed', 'Musician Needed', 'Audition', 'Showcase', 'Live Show', 'Stage Play', 'Movie', 'Short Movie', 'TV Show', 'Open Mic', 'DJ set', 'Stand Up Comedy', 'Workshop', 'Presentation', 'Vernissage', 'Collective Exhibition'];

// Types that appear on the public landing page (must match server/controllers/oppController.js)
export const PUBLIC_LANDING_TYPES: Opportunity['type'][] = ['Gig', 'Event', 'Live Show', 'Stage Play', 'Open Mic', 'DJ set', 'Stand Up Comedy', 'Showcase', 'Workshop', 'Presentation', 'Vernissage', 'Collective Exhibition'];

export interface Salary {
    currency: '$' | '€' | '£';
    amount: number;
    period: 'year' | 'day' | 'hour' | 'flat rate';
}

export type ApplicationStatus = 'draft' | 'submitted' | 'reviewed' | 'shortlisted' | 'accepted' | 'rejected';

export interface Application {
    id: string;
    applicant: User;
    timestamp: string;
    status: ApplicationStatus;
    coverLetter: string;
    submissionLinks: string[];
    applyingAs: {
        individual: boolean;
        bands: { id: string; name: string }[];
    };
}

export interface Comment {
    id: string;
    author: Partial<User> & { handle: string; name: string; avatarUrl: string };
    text: string;
    timestamp: string;
}

export interface Band {
    id: string;
    name: string;
    city: string;
    region?: string;
    country?: string;
    avatarUrl: string;
    bannerUrl?: string;
    isClaimed: boolean;
    members: { handle: string; name: string; avatarUrl: string; title: string }[];
    genres: string[];
    instruments: string[];
    bio?: string;
    socialLinks?: {
        facebook?: string;
        instagram?: string;
        [key: string]: string | undefined;
    };
    photos?: Photo[];
    embeddedMedia?: EmbeddedMedia[];
    fans?: string[];
    rider?: string;
    riderFiles?: RiderFile[];
}

export interface RiderFile {
    id: string;
    name: string;
    url: string;
}

export type SearchResult = (Opportunity & { resultType: 'opportunity' }) | (User & { resultType: 'user' }) | (Band & { resultType: 'band' });

export interface Lineup {
    id: string;
    opportunityId: string;
    eventDays: number;
    defaultTransitionTime: number;
    days: LineupDay[];
}

export interface LineupDay {
    date: string;
    slots: LineupSlot[];
}

export interface LineupSlot {
    id: string;
    artist: { handle: string; name: string; avatarUrl: string };
    artistImageUrl?: string;
    startTime: string;
    endTime: string;
    transitionTimeAfter: number;
    isMain: boolean;
}

export type FlyerTheme = 'dark' | 'light';
export type FlyerFont = 'Modern' | 'Classic' | 'Impact' | 'Roboto Slab' | 'Playfair Display' | 'Oswald' | 'Anton' | 'Urbanist' | 'Russo One' | 'Jersey 10';

export interface FlyerConfig {
    id: string;
    flyerTitle: string;
    flyerLocation: string;
    flyerDate: string;
    artistImageSize: number;
    lineupFontSize: number;
    theme: FlyerTheme;
    font: FlyerFont;
    titleColor: string;
    showMainTitle: boolean;
    showMainDate: boolean;
    showMainLocation: boolean;
    showArtistImages: boolean;
    showPerformanceHours: boolean;
    showDayBadge: boolean;
    showDayBadgeBg?: boolean;
    showArtistsOnGraphic: boolean;
    headerLayout?: { x: number; y: number; scale: number };
    artistLayouts: Record<string, { x: number; y: number; scale: number }>;
    graphicPageBackground?: string;
    lineupPageBackground?: string;
    bannerBackground?: string;
    footerBackground?: string;
    graphicBackground?: string;
    lineupBackground?: string;
}

export interface SavedFlyer {
    id: string;
    userId: string;
    opportunityId: string;
    opportunityTitle: string;
    config: FlyerConfig;
    lineup: Lineup;
    createdAt: string;
}

export type ChurnReason = 'not_useful' | 'too_expensive' | 'difficult_to_use' | 'found_gig_elsewhere' | 'privacy_concerns' | 'other';

export interface Message {
    id: string;
    senderHandle: string;
    receiverHandle: string;
    text: string;
    timestamp: string;
    isRead: boolean;
}

export interface Notification {
    id: string;
    type: 'endorse' | 'amplify' | 'application' | 'withdrawal' | 'match' | 'accepted' | 'rejected' | 'comment' | 'invitation' | 'message' | 'collaboration_add' | 'create_band_prompt' | 'attendance';
    user: Partial<User> & { handle: string; name: string; avatarUrl: string };
    recipientHandle: string;
    opportunity?: { id: string; title: string };
    band?: { id: string; name: string };
    timestamp: string;
    isRead: boolean;
}
